import React, { createContext, useCallback, useContext, useState } from 'react';

import { useSearchParams } from 'react-router-dom';
import { useLangNavigate } from 'shared/hooks';

import { useRouteContext } from './RouterContext';

export const MODAL_TYPES = {
  LOGIN: 'LOGIN',
  SIGNUP: 'SIGNUP',
  PASSWORD: 'PASSWORD',
  RESET: 'RESET',
  FIND_EMAIL: 'FIND_EMAIL',
  // 소셜 가입 직후 프로필 완성(온보딩)
  ONBOARDING: 'ONBOARDING',
  NONE: 'NONE',
};

interface ModalContextType {
  modalType: string;
  openModal: (type: string) => void;
  closeModal: () => void;
  closeModalWithRedirectClear: () => void;
  MODAL_TYPES: typeof MODAL_TYPES;
}

const defaultContextValue: ModalContextType = {
  modalType: MODAL_TYPES.NONE,
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  openModal: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeModal: () => {},
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  closeModalWithRedirectClear: () => {},
  MODAL_TYPES,
};

const ModalContext = createContext<ModalContextType>(defaultContextValue);

export function ModalProvider({ children }: { children: React.ReactNode }) {
  const [modalType, setModalType] = useState(MODAL_TYPES.NONE);
  const { navigateWithLang } = useLangNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { setRedirectPath } = useRouteContext();

  const openModal = useCallback(
    (type: string) => {
      if (searchParams.get('state')) {
        searchParams.delete('state');
        navigateWithLang(`/?${searchParams.toString()}`, { replace: true });
      }
      setModalType(type);
    },
    [navigateWithLang, searchParams],
  );

  /**
   * @description
   * 모달을 닫고 URL에서 state와 signup 파라미터를 제거합니다.
   * 일반적인 모달 닫기 동작에 사용됩니다.
   *
   * @example
   * const { closeModal } = useModal();
   * closeModal();
   */
  const closeModal = useCallback(() => {
    if (searchParams.get('state')) {
      searchParams.delete('state');
      setSearchParams(searchParams);
    }
    if (searchParams.get('signup')) {
      searchParams.delete('signup');
      setSearchParams(searchParams);
    }
    setModalType(MODAL_TYPES.NONE);
  }, [searchParams, setSearchParams]);

  /**
   * @description
   * 모달을 닫고 URL에서 state, redirect, signup 파라미터를 모두 제거합니다.
   * 로그인이 필요한 페이지에서 로그인 모달을 닫을 때 redirect 경로를 제거하기 위해 사용됩니다.
   *
   * @example
   * const { closeModalWithRedirectClear } = useModal();
   * closeModalWithRedirectClear();
   */
  const closeModalWithRedirectClear = useCallback(() => {
    setRedirectPath(null);
    if (searchParams.get('state')) {
      searchParams.delete('state');
      setSearchParams(searchParams);
    }
    if (searchParams.get('redirect')) {
      searchParams.delete('redirect');
      setSearchParams(searchParams);
    }
    if (searchParams.get('signup')) {
      searchParams.delete('signup');
      setSearchParams(searchParams);
    }
    setModalType(MODAL_TYPES.NONE);
  }, [searchParams, setSearchParams, setRedirectPath]);

  return (
    <ModalContext.Provider value={{ modalType, openModal, closeModal, closeModalWithRedirectClear, MODAL_TYPES }}>
      {children}
    </ModalContext.Provider>
  );
}

export const useModal = () => {
  return useContext(ModalContext);
};
